import moment from 'moment';
import { pvrSettings } from '../../config';
import i18n from '../../i18n';
import recordingBrand from '../../icons/recording.svg';
import { useProgress } from '../../queries/ats/me/hooks';
import { ProgramPlaybackOption } from '../../queries/ats/titles/types/playback-option';
import { Recording } from '../../queries/recordings/types';
import { groupIsSeries } from '../../queries/recordings/utils';
import { isDeleteModeManual } from '../../utils/pvrUtils';
import { BadgeTypes } from '../Badge';

export function usePvrAsset(group: Recording.Group.Union, showProgress = false, playOnHover = false) {
  const asset = groupIsSeries(group) ? group.episodes[0] : group.asset;
  const startTime = moment(asset.beginTime).subtract(parseInt(asset.beginOffset, 10), 'minutes');
  const endTime = moment(asset.endTime).add(parseInt(asset.endOffset, 10), 'minutes');

  const progress = useProgress({
    masterId: group.programId,
    playbackOptions: [createFakePlaybackOption(asset)],
    duration: endTime.diff(startTime, 'seconds'),
  });

  const title = getTitle(group, showProgress);
  const link = getLink(group, showProgress);
  const showPin = groupIsSeries(group)
    ? isDeleteModeManual(group.episodes[0].deleteMode)
    : isDeleteModeManual(group.asset.deleteMode);

  return {
    title,
    id: group.pvrId,
    programId: group.programId,
    subtitle: getSubtitle(group, showProgress),
    link: link,
    getBrand: (showChannel?: boolean) => getBrand(group, showChannel),
    progress: showProgress ? progress : undefined,
    image: {
      src: group.pictures?.[0].href || '',
      alt: title.join(' - '),
    },
    badge: getBadge(group),
    showPin,
    playOnHover,
  } as const;
}

function createFakePlaybackOption(recording: Recording.Query.Movie): ProgramPlaybackOption {
  return {
    id: recording.pvrId,
    platformTitleId: recording.programId,
    providerIdentifier: recording.channelId,
    startTime: moment(recording.beginTime).format(),
    endTime: moment(recording.endTime).format(),
    sourceId: recording.pvrId,
    supportsCatchup: false,
    lifetimeId: recording.lifetimeId,
    availableFrom: moment().subtract(1, 'day').format(),
    availableTo: moment().add(1, 'day').format(),
    hasTrailer: false,
    media: [],
    audioLanguages: [],
    subtitleLanguages: [],
    links: {
      web: null,
      native: null,
    },
    price: [],
    distributor: '',
    casting: {
      airPlay: true,
      chromecast: true,
    },
    providerName: recording.channelName,
    providerTitleId: recording.channelNo,
    serviceType: 'program',
    channelId: recording.channelId,
    channelName: recording.channelName,
  };
}

function getBrand(group: Recording.Group.Union, showChannel?: boolean) {
  if (!showChannel || !group.channel) {
    return {
      src: recordingBrand,
      alt: 'Recording',
    };
  }

  return {
    src: group.channel.pictures[1].href ?? group.channel.pictures[0].href,
    alt: group.channel?.name ?? '',
  };
}

function getLink(group: Recording.Group.Union, showProgress: boolean) {
  if (groupIsSeries(group)) {
    const episode = group.episodes[0];

    return showProgress
      ? `/details/recording/single/${episode.programId}?orderByProviderId=${episode.channelId}`
      : `/details/recording/series/${episode.seriesId}?orderByProviderId=${episode.channelId}`;
  }

  return `/details/recording/single/${group.programId}?orderByProviderId=${group.channel?.contentId}`;
}

function getTitle(group: Recording.Group.Union, showProgress: boolean) {
  if (groupIsSeries(group)) {
    const episode = group.episodes[0];

    if (episode.seasonNum && episode.subNum) {
      return showProgress ? [`${group.pvrName}`, `S${episode.seasonNum} E${episode.subNum}`] : [group.pvrName];
    }

    return [group.pvrName];
  }

  return [group.pvrName];
}

function getSubtitle(group: Recording.Group.Union, showProgress: boolean) {
  return groupIsSeries(group) && !showProgress
    ? `${i18n.t('episodes', { count: group.episodes.length, ns: 'pvr' })}`
    : getGenre(group);
}

function getGenre(group: Recording.Group.Union) {
  if (group.genreNames) {
    return group.genreNames[0];
  }

  return i18n.t('movie', { ns: 'translations' });
}

function getBadge(group: Recording.Group.Union) {
  const asset = groupIsSeries(group) ? group.episodes[0] : group.asset;
  const startTime = moment(asset.beginTime).subtract(parseInt(asset.beginOffset, 10), 'minutes');
  const endTime = moment(asset.endTime).add(parseInt(asset.endOffset, 10), 'minutes');
  const now = moment();

  if (now.isAfter(startTime) && now.isBefore(endTime)) {
    return {
      text: i18n.t<string>('is recording'),
      type: 'primary',
    } as const;
  }

  const badgeText = getBadgeText(group);

  if (!badgeText) {
    return;
  }

  if (group.unwatchedCount >= 1) {
    return {
      text: badgeText,
      type: BadgeTypes.New,
    };
  }

  return {
    text: badgeText,
    type: BadgeTypes.Expires,
  };
}

function getBadgeText(group: Recording.Group.Union) {
  const daysLeft = group.lowestExpirationTime;
  const limit = pvrSettings.expiration;

  if (group.unwatchedCount >= 1) {
    return i18n.t('new', { count: group.unwatchedCount, ns: 'pvr' });
  }

  if (!daysLeft || (daysLeft > limit && daysLeft >= 0)) {
    return undefined;
  }

  if (!daysLeft) {
    return '';
  }

  if (daysLeft <= 2) {
    return `${Math.floor(daysLeft * 24)} ${i18n.t('rent hours suffix', { ns: 'translations' })} ${i18n.t(
      'time remaining',
      {
        ns: 'translations',
      },
    )}`;
  }

  return `${Math.floor(daysLeft)} ${i18n.t('day', { count: 2, ns: 'translations' })} ${i18n.t('time remaining', {
    ns: 'translations',
  })}`;
}
